import React from 'react';

import LogoIcon from 'icons/ihasco-logo-mark.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import RowBar from 'components/application/RowBar';
import SearchField from 'components/application/SearchField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import ToggleableSelect from 'components/selectors/ToggleableSelect';

import {
  asCourseObject,
  defaultCourse,
} from 'components/helpers/resources/courses';

export const isValidNewOption = (courseOption) =>
  !!courseOption && courseOption.trim().length !== 0;

export default function CoursesBar({
  actionButtonsVisible,
  coursesCount,
  coursesSearch,
  onSearchInputChange,
  onSearchReset,
  openSidePanel,
  resetRequestError,
  setCurrentCourse,
  setSidePanelContext,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.CoursesBar',
  });

  const { t: i18nTCourses } = useTranslation('common', {
    keyPrefix: 'courses',
  });

  const handleChange = (selected) => {
    let newCurrentCourse;

    if (selected.value.resource) {
      const course = selected.value.resource;
      newCurrentCourse = {
        ...asCourseObject({
          course,
          eLearningAllowed: currentActor.isAllowedFeature('e_learning'),
          currencyCode: currentActor.division.attributes.currencyCode,
        }),
        id: null,
        courseCompanyRoles: [],
        originalId: course.id,
      };
    } else {
      newCurrentCourse = {
        ...defaultCourse,
        name: voca.capitalize(selected.value),
      };
    }

    setSidePanelContext('new');
    resetRequestError();
    setCurrentCourse(newCurrentCourse);
    openSidePanel();
  };

  const handleFocus = () => {
    setCurrentCourse(defaultCourse);
  };

  const wasCopiedToAccount = (courseOption) =>
    courseOption.meta.wasCopiedToAccount;

  const renderSublabel = (courseOption) =>
    (
      currentActor.isAllowedFeature('e_learning') &&
      !wasCopiedToAccount(courseOption) &&
      !!courseOption.relationships.eLearningCourse.data
    ) ?
      <div className='tw-flex tw-h-4 tw-items-center'>
        <LogoIcon className='tw-mr-1' height={16} width={16} />
        <span className='tw-text-s tw-font-normal tw-tracking-wide tw-text-grey-600'>
          {i18nTCourses('eLearningAvailable')}
        </span>
      </div>
    : null;

  return (
    <RowBar
      actions={
        actionButtonsVisible && (
          <ToggleableSelect
            buttonText={i18nTCourses('addNewCourse')}
            createOptionPosition='first'
            isCreatable={true}
            isOptionDisabled={wasCopiedToAccount}
            isValidNewOption={isValidNewOption}
            name='course'
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={i18nT('select.placeholder')}
            renderSublabel={renderSublabel}
            url='/standard_courses'
          />
        )
      }
      content={
        coursesCount > 0 && (
          <SearchField
            additionalClasses='search-field__lg'
            name='coursesSearch'
            onChange={onSearchInputChange}
            onReset={onSearchReset}
            placeholder={i18nT('search.placeholder')}
            value={coursesSearch}
          />
        )
      }
      modifiers={[
        'border-top-curved',
        'border-bottom-none',
        'flex-align-items-ie-fix',
      ]}
    />
  );
}

CoursesBar.propTypes = {
  actionButtonsVisible: PropTypes.bool.isRequired,
  setSidePanelContext: PropTypes.func.isRequired,
  setCurrentCourse: PropTypes.func.isRequired,
  openSidePanel: PropTypes.func.isRequired,
  coursesSearch: PropTypes.string.isRequired,
  coursesCount: PropTypes.number.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  onSearchReset: PropTypes.func.isRequired,
  resetRequestError: PropTypes.func.isRequired,
};
